import { insertYuzuPlainTextPaste } from './yuzuTextPaste'

export function parseGreetingCardMessageLimit(value: unknown) {
  const limit = Number(value)
  return Number.isSafeInteger(limit) && limit > 0 ? limit : Infinity
}

export function greetingCardMessageWarningThreshold(limit: number) {
  return limit > 100 ? limit - 100 : Math.ceil(limit * 0.9)
}

/** Count text, including spaces and line breaks, rather than HTML markup. */
export function greetingCardMessageText(html: string) {
  const element = document.createElement('template')
  element.innerHTML = html
  function read(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? ''
    if (node.nodeName === 'BR') return '\n'
    const children = [...node.childNodes]
    if (children.length === 1 && children[0]?.nodeName === 'BR') return ''
    return children
      .map((child, index) => {
        const isBlock = /^(DIV|P|LI)$/.test(child.nodeName)
        const previousIsBlock = /^(DIV|P|LI)$/.test(
          children[index - 1]?.nodeName ?? '',
        )
        return (
          (index > 0 && (isBlock || previousIsBlock) ? '\n' : '') + read(child)
        )
      })
      .join('')
  }
  return read(element.content).replaceAll('\u200B', '')
}

export function greetingCardMessageLength(text: string) {
  return Array.from(text.replaceAll('\u200B', '')).length
}

/** Shared by both contenteditable message editors; loaded text is never truncated. */
export function createGreetingCardMessageInput(
  getLimit: () => number = () => Infinity,
) {
  let previousHtml = ''

  function remember(event: Event) {
    previousHtml = (event.currentTarget as HTMLElement).innerHTML
  }

  function remaining(element: HTMLElement) {
    const selection = element.ownerDocument.getSelection()
    let selected = 0
    if (
      selection?.rangeCount &&
      element.contains(selection.getRangeAt(0).commonAncestorContainer)
    ) {
      const fragment = document.createElement('div')
      fragment.append(selection.getRangeAt(0).cloneContents())
      selected = greetingCardMessageLength(
        greetingCardMessageText(fragment.innerHTML),
      )
    }
    return Math.max(
      0,
      getLimit() -
        greetingCardMessageLength(greetingCardMessageText(element.innerHTML)) +
        selected,
    )
  }

  function beforeInput(event: InputEvent) {
    if (event.isComposing || event.inputType.includes('Composition')) return
    remember(event)
    const inserted =
      event.inputType === 'insertParagraph' ||
      event.inputType === 'insertLineBreak'
        ? '\n'
        : event.data
    if (
      event.inputType.startsWith('insert') &&
      inserted &&
      greetingCardMessageLength(inserted) >
        remaining(event.currentTarget as HTMLElement)
    ) {
      event.preventDefault()
    }
  }

  function paste(event: ClipboardEvent) {
    if (!event.clipboardData) return
    event.preventDefault()
    remember(event)
    const element = event.currentTarget as HTMLElement
    const text = event.clipboardData
      .getData('text/plain')
      .replace(/\r\n?/g, '\n')
    const capacity = remaining(element)
    const accepted = Array.from(text).slice(0, capacity).join('')
    if (!accepted) return
    // Selecting all visible text can leave an empty paragraph outside the range.
    // Include that wrapper so a full replacement does not retain an extra newline.
    if (Number.isFinite(capacity) && capacity === getLimit()) {
      const range = document.createRange()
      range.selectNodeContents(element)
      const selection = element.ownerDocument.getSelection()
      selection?.removeAllRanges()
      selection?.addRange(range)
    }
    insertYuzuPlainTextPaste(element, accepted)
    element.dispatchEvent(new Event('input', { bubbles: true }))
  }

  function input(event: Event) {
    if ((event as InputEvent).isComposing) return
    const element = event.currentTarget as HTMLElement
    const previousLength = greetingCardMessageLength(
      greetingCardMessageText(previousHtml),
    )
    if (
      greetingCardMessageLength(greetingCardMessageText(element.innerHTML)) >
      Math.max(getLimit(), previousLength)
    ) {
      element.innerHTML = previousHtml
    }
    remember(event)
  }

  return { remember, beforeInput, paste, input }
}
